import { motion } from "framer-motion";
import { Logo } from "./Logo";
import { ContactForm } from "./ContactForm";

const ease = [0.22, 1, 0.36, 1];

const Rise = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease }}
  >
    {children}
  </motion.div>
);

// The subsection heading rides a parallax layer: Sheets translates anything tagged
// [data-nav-heading] relative to the camera, so it moves at a different rate than
// the copy as a section slides in.
const Heading = ({ children }) => (
  <div data-nav-heading style={{ willChange: "transform, filter" }}>{children}</div>
);

const Wrap = ({ children, testid }) => (
  <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12" data-testid={testid}>{children}</div>
);

// the source sits behind the headline and shuffles into place; each word is its
// own inline-block span (React-owned, so transforms apply and survive re-render),
// and the Sheets line controller collects them to drive the shuffle/reveal/peel
const HERO_CODE = `import { Sheets } from "./Sheets";
import { SweptLine, Dimension } from "./motion";
export const Hero=()=>(
<section className="flex flex-col justify-center">
<p className="label">01 / Purpose</p>
<h1 className="font-light text-7xl">
<SweptLine delay={.15}>We manage how</SweptLine>
<SweptLine delay={.4}>developments</SweptLine>
<SweptLine delay={.65}><span className="accent">face the public</span></SweptLine>
</h1><button className="cta">Start a campaign →</button>
<p className="sub">Strategy, landing pages and paid media.</p></section>);
export default Hero;
<Sheets panels={[Hero, Services, Who, Contact]} />`;
const CODE_TOKENS = HERO_CODE.split(/(\s+)/);

export const PurposePanel = () => {
  return (
    <section className="min-h-full flex flex-col justify-center py-8">
      <Wrap testid="hero-section">
        {/* the eyebrow is revealed content: it stays hidden while the source shuffles,
            then the falling line wipes it in on its way to the headline (useHeroLine).
            initial clip hides it before the first draw. */}
        <p data-hero-eyebrow className="label text-[#555555] mb-6 md:mb-10" style={{ clipPath: "inset(0 0 100% 0)" }}>01 / Purpose</p>
        {/* the headline, its source, and the dimension line live in one unit so the peel
            clip and the line share a coordinate space and stay aligned (see useHeroLine) */}
        <div className="relative" data-hero-unit>
          <div className="relative w-fit" data-hero-zone>
            <pre
              data-hero-code
              aria-hidden="true"
              className="hero-code absolute inset-0 overflow-hidden m-0 whitespace-pre text-[#555555]"
            >
              {CODE_TOKENS.map((t, i) =>
                t === "" || /^\s+$/.test(t) ? t : (
                  <span key={i} className="cw">{t}</span>
                )
              )}
            </pre>
            <h1
              data-hero-headline
              className="relative bg-[#f9f9f7] font-light tracking-[-0.04em] leading-[0.95] text-[2.75rem] min-[960px]:text-[5.5rem] whitespace-nowrap"
              style={{ paddingRight: "250px", clipPath: "path('M 0 0 L 0 0 Z')" }}
              data-testid="hero-statement"
            >
              We manage how<br />developments<br /><span className="accent">face the public</span>
            </h1>
          </div>
          <svg
            data-hero-line
            className="pointer-events-none absolute inset-0 z-20"
            style={{ overflow: "visible" }}
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <g data-hero-line-g style={{ opacity: 0 }}>
              <path data-hero-path fill="none" stroke="#ff4f00" strokeWidth="3.5" strokeLinecap="butt" />
              <line data-hero-cap-l stroke="#ff4f00" strokeWidth="3.5" />
              <line data-hero-cap-r stroke="#ff4f00" strokeWidth="3.5" />
            </g>
          </svg>
        </div>
        <Rise delay={2.1} className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-14">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("sheet-goto", { detail: 3 }))}
            className="inline-flex items-center gap-4 bg-[#111111] text-[#f9f9f7] px-8 py-4 label tracking-[0.24em] transition-colors duration-300 hover:bg-[#ff4f00] w-fit"
            data-testid="hero-cta-button"
          >
            Start a campaign <span aria-hidden="true">&rarr;</span>
          </button>
          <p className="text-sm text-[#555555] max-w-xs leading-relaxed">
            Strategy, landing pages and paid media that sell off-the-plan developments for property developers.
          </p>
        </Rise>
      </Wrap>
    </section>
  );
};

const services = [
  { num: "A", name: "Campaign strategy", desc: "Positioning, the pricing story, and who to reach in what order. A plan you can measure in conversions, not enquiries." },
  { num: "B", name: "Conversion landing pages", desc: "With a landing page, the hard part isn’t the building, it’s knowing what to leave off. We build one page for one project, write it for the target audience you actually want to reach, and cut anything that isn’t helping them enquire. It loads fast, and it points every visitor at one thing: making an enquiry." },
  { num: "C", name: "Paid media management", desc: "All of your ad accounts, with no agency in the middle and no markup on your spend. Reported every week, to the dollar." },
];

export const ServicesPanel = () => (
  <section className="min-h-full flex flex-col justify-start pt-12 pb-6">
    <Wrap testid="services-section">
      <Rise><p className="label text-[#555555] mb-3">02 / What we do</p></Rise>
      <Heading>
        <p className="text-lg md:text-2xl font-light tracking-[-0.02em] text-[#111111] max-w-md leading-snug mb-8 md:mb-12">Three services, in the order a launch actually needs them.</p>
      </Heading>
      <div className="border-t rule">
        {services.map((s, i) => (
          <Rise key={s.num} delay={0.15 + i * 0.1}>
            <div className="group border-b rule py-6 md:py-8 grid grid-cols-12 gap-4 md:gap-8 items-start" data-testid={`service-row-${s.num.toLowerCase()}`}>
              <span className="col-span-2 md:col-span-1 label text-[#555555]">{s.num}.</span>
              <h2 className="col-span-10 md:col-span-6 font-light tracking-[-0.03em] leading-none text-2xl md:text-4xl lg:text-5xl transition-colors duration-300 group-hover:text-[#ff4f00]">
                {s.name}
              </h2>
              <p className="col-span-10 col-start-3 md:col-span-4 md:col-start-9 text-sm text-[#555555] leading-relaxed">{s.desc}</p>
            </div>
          </Rise>
        ))}
      </div>
    </Wrap>
  </section>
);

const whoPoints = [
  { title: "Who runs it", body: "Frontage is one person with three decades of building for the web, and that’s the whole pitch. You’re not hiring a platform, or a team of juniors learning on your launch. You’re hiring someone who can tell what will work before you’ve spent a dollar finding out. The person who pitches your development is the person who plans it, designs it and writes the pages." },
  { title: "Numbers over adjectives", body: "Spend, cost per lead and conversion rate, every week, to the dollar. You’ll always know what your money did." },
  { title: "Small on purpose", body: "A handful of projects a year, so yours gets the attention it deserves." },
];

export const WhoPanel = () => (
  <section className="min-h-full flex flex-col justify-start pt-12 pb-6" data-testid="who-section">
    <Wrap testid="who-section">
      <Rise><p className="label text-[#555555] mb-4">03 / Who you deal with</p></Rise>
      <Heading>
        <h2 className="font-light tracking-[-0.03em] leading-[1.02] text-3xl md:text-5xl lg:text-6xl max-w-[15ch]">
          One person. Not an agency floor.
        </h2>
      </Heading>
      <Rise delay={0.15}>
        <p className="mt-7 md:mt-8 max-w-2xl text-sm md:text-base text-[#555555] leading-relaxed">
          Everyone’s using the same tools now. It’s the thing nobody in this industry says out loud. The software that used to separate a good agency from a bad one is free, or close to it. Being able to do the work isn’t what sets anyone apart anymore. It’s whether they know which of a hundred small decisions actually sells the properties, and which ones just look like work. That part isn’t a tool. It’s judgement, and judgement is the one thing that’s still expensive.
        </p>
      </Rise>
      <div className="mt-5 md:mt-6 border-t rule">
        {whoPoints.map((pt, i) => (
          <Rise key={pt.title} delay={0.3 + i * 0.1}>
            <div className={`pt-5 md:pt-6 ${i < whoPoints.length - 1 ? "pb-5 md:pb-6 border-b rule" : ""}`} data-testid={`who-point-${i}`}>
              <div className="flex items-center gap-4">
                <span aria-hidden="true" className="block w-4 h-[2px] bg-[#ff4f00] shrink-0" />
                <h3 className="text-lg md:text-xl font-normal tracking-[-0.01em]">{pt.title}</h3>
              </div>
              <p className="mt-2 pl-8 text-sm text-[#555555] leading-relaxed max-w-xl">{pt.body}</p>
            </div>
          </Rise>
        ))}
      </div>
    </Wrap>
  </section>
);

export const ContactPanel = () => (
  <section className="min-h-full flex flex-col justify-start pt-12 pb-6" data-testid="contact-section">
    <Wrap>
      <Rise>
        <p className="label text-[#555555] mb-4">04 / Contact</p>
      </Rise>
      <Heading>
        <h2 className="font-light tracking-[-0.03em] leading-[1.02] text-3xl md:text-5xl max-w-xl" data-testid="contact-heading">
          Tell us what you&rsquo;re launching.
        </h2>
      </Heading>
      <Rise delay={0.5}>
        <p className="mt-5 text-sm text-[#555555] max-w-md leading-relaxed">
          It comes straight to the person who&rsquo;ll do the work, not an inbox queue, and you&rsquo;ll have a considered reply inside one working day.
        </p>
      </Rise>
      <Rise delay={0.2} className="mt-12 md:mt-16">
        <ContactForm />
      </Rise>
    </Wrap>
    <Rise delay={0.35}>
      <footer className="mx-auto w-full max-w-[1440px] px-6 md:px-12 mt-4 md:mt-6 flex justify-end">
        <div className="flex items-center gap-4" style={{ transform: "translateY(-10px)" }}>
          <Logo variant="monogram" mono className="h-6 w-6" />
          <span className="label text-[#555555]">&copy; 2026 Frontage</span>
        </div>
      </footer>
    </Rise>
  </section>
);
